import type { FC, ReactNode } from 'react'
import type { ConceptStageIcon } from '@/constants/content'

import { Hand, Image as ImageIcon, MapPin, Mic } from 'lucide-react'
import { BuckyMascot } from '@/components/generals/bucky-mascot'

import { concept } from '@/constants/content'

// Small drawn screens, one per stage. Illustrations only.
const MiniScreen: FC<{ icon: ConceptStageIcon }> = ({ icon }): ReactNode => {
    return (
        <div aria-hidden className='relative aspect-[16/10] w-full overflow-hidden rounded-lg bg-[radial-gradient(80%_80%_at_50%_40%,#1B3350,#08131F)] ring-1 ring-soft-white/10'>
            {icon === 'attract' && (
                <>
                    <span className='bubble-float absolute top-[42%] left-[18%] size-[16%] rounded-full bg-orange' />
                    <span className='bubble-float absolute top-[28%] left-[42%] size-[12%] rounded-full bg-orange [animation-delay:0.5s]' />
                    <span className='bubble-float absolute top-[46%] left-[64%] size-[18%] rounded-full bg-orange [animation-delay:1s]' />
                    <span className='absolute bottom-[10%] left-1/2 -translate-x-1/2 rounded-full bg-orange px-2 py-0.5 text-[9px] font-bold text-navy'>
                        Tap to play
                    </span>
                </>
            )}

            {icon === 'engage' && (
                <>
                    <span className='absolute top-[34%] left-[22%] size-[14%] rounded-full bg-orange/80' />
                    <span className='absolute top-[50%] left-[46%] size-[12%] rounded-full bg-orange/80' />
                    <Hand className='absolute top-[42%] left-[30%] size-[26%] text-soft-white' />
                    <span className='absolute right-[6%] bottom-[8%] block size-[24%] rounded-full bg-navy-900 p-px ring-2 ring-orange/70'>
                        <BuckyMascot size={24} decorative className='size-full' />
                    </span>
                </>
            )}

            {icon === 'discover' && (
                <div className='absolute inset-[14%] flex gap-[6%] rounded-md bg-navy-900/90 p-[5%] ring-1 ring-soft-white/10'>
                    <span className='grid aspect-square h-full place-items-center rounded bg-[linear-gradient(135deg,#31527A,#1B3350)] text-orange'>
                        <ImageIcon className='size-1/2' />
                    </span>
                    <span className='flex flex-1 flex-col justify-center gap-1.5'>
                        <span className='h-1.5 w-4/5 rounded-full bg-soft-white/70' />
                        <span className='h-1.5 w-3/5 rounded-full bg-soft-white/30' />
                        <span className='mt-1 h-2.5 w-2/5 rounded-full bg-orange' />
                    </span>
                </div>
            )}

            {icon === 'connect' && (
                <div className='absolute inset-[12%] flex flex-col justify-center gap-[8%]'>
                    <span className='ml-auto h-[22%] w-[55%] rounded-md rounded-tr-sm bg-orange/90' />
                    <span className='h-[22%] w-[70%] rounded-md rounded-tl-sm bg-navy-700 ring-1 ring-soft-white/10' />
                    <span className='flex items-center gap-1 text-cool-grey-light'>
                        <span className='grid size-4 place-items-center rounded-full bg-navy-700'>
                            <Mic className='size-2.5' />
                        </span>
                        <span className='h-1 w-1/3 rounded-full bg-soft-white/20' />
                    </span>
                </div>
            )}

            {icon === 'guide' && (
                <>
                    <svg viewBox='0 0 160 100' className='absolute inset-0 size-full'>
                        <path d='M20 80 C 60 80, 60 40, 100 42 S 130 30, 132 28' fill='none' stroke='#FF8A3D' strokeWidth='3' strokeDasharray='6 6' strokeLinecap='round' />
                        <circle cx='20' cy='80' r='5' fill='#F4F7FA' />
                    </svg>
                    <MapPin className='absolute top-[10%] right-[10%] size-[26%] fill-orange text-navy' />
                </>
            )}
        </div>
    )
}

export const ConceptHowItWorks: FC = (): ReactNode => {
    return (
        <div id='how-bucky-works' className='mt-24 scroll-mt-28 sm:mt-32'>
            <div className='reveal max-w-2xl'>
                <h3 className='text-[1.75rem] leading-tight font-bold tracking-tight text-soft-white sm:text-4xl'>
                    {concept.howItWorks.heading}
                </h3>

                <p className='mt-3 text-lg text-cool-grey-light'>
                    {concept.howItWorks.intro}
                </p>
            </div>

            <ol className='relative mt-10 grid gap-5 lg:grid-cols-5 lg:gap-4'>
                <span aria-hidden className='absolute top-0 bottom-0 left-5 w-px bg-linear-to-b from-orange/70 via-soft-white/15 to-transparent lg:top-5 lg:right-[10%] lg:bottom-auto lg:left-[10%] lg:h-px lg:w-auto lg:bg-linear-to-r lg:from-orange/60 lg:via-soft-white/20 lg:to-orange/40' />

                {concept.howItWorks.stages.map(stage => (
                    <li key={stage.number} className='reveal relative flex gap-4 lg:flex-col lg:gap-4'>
                        <span className='relative z-10 grid size-10 shrink-0 place-items-center rounded-full bg-orange font-heading text-sm font-bold text-navy shadow-[0_0_0_6px_#14283D] lg:mx-auto'>
                            {stage.number}
                        </span>

                        <div className='flex-1 rounded-xl border border-soft-white/10 bg-navy-900/60 p-4'>
                            <MiniScreen icon={stage.icon} />

                            <h4 className='mt-4 font-heading text-lg font-bold text-soft-white'>
                                {stage.title}
                            </h4>

                            <p className='mt-1.5 text-sm leading-relaxed text-cool-grey-light'>
                                {stage.text}
                            </p>
                        </div>
                    </li>
                ))}
            </ol>
        </div>
    )
}
